import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, RefreshCw } from 'lucide-react';
import Markdown from 'react-markdown';
import type { Digest, DigestType } from '@cerebro-diem/core';
import { supabase } from '../../services/supabase';
import { invokeEdgeFunction } from '../../services/edgeFunctions';
import { errorMessage } from '../../services/errors';
import { queryKeys } from '../../services/queries';
import { digestAge } from '../../services/digests';
import { tapTarget } from '../../components/styles';
import OutcomesCheck from '../outcomes/OutcomesCheck';

export default function DigestPage() {
  const queryClient = useQueryClient();

  const digestsQuery = useQuery({
    queryKey: queryKeys.digestList,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('digests')
        .select('*')
        .order('generated_at', { ascending: false })
        .limit(30);

      if (error) throw error;
      return (data ?? []) as Digest[];
    },
  });
  const digests = digestsQuery.data ?? [];
  // The review's step for the outcomes list goes with the newest weekly review only.
  const newestWeekly = digests.find((digest) => digest.type === 'weekly')?.id;

  // The function binds to the caller's session; only the digest type is sent.
  const generateMutation = useMutation({
    mutationFn: (type: DigestType) => invokeEdgeFunction('generate-digest', { type }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.digests }),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Mail className="w-8 h-8 text-primary" />
          <h1 className="text-2xl font-bold">Digests</h1>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => generateMutation.mutate('daily')}
            disabled={generateMutation.isPending}
            className={`flex items-center gap-2 ${tapTarget} px-4 py-2 bg-primary text-on-primary rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50`}
          >
            <RefreshCw className={`w-4 h-4 ${generateMutation.isPending ? 'animate-spin' : ''}`} />
            Generate Daily
          </button>
          <button
            type="button"
            onClick={() => generateMutation.mutate('weekly')}
            disabled={generateMutation.isPending}
            className={`flex items-center gap-2 ${tapTarget} px-4 py-2 bg-secondary text-on-secondary rounded-xl hover:bg-secondary/90 transition-colors disabled:opacity-50`}
          >
            Generate Weekly
          </button>
        </div>
      </div>

      {generateMutation.isPending && <p className="text-sm text-on-surface-variant">Generating digest…</p>}
      {generateMutation.isError && (
        <p role="alert" className="text-sm text-error">
          Digest not generated: {errorMessage(generateMutation.error)}
        </p>
      )}

      {digestsQuery.isError ? (
        <p className="text-sm text-error">Could not load digests: {errorMessage(digestsQuery.error)}</p>
      ) : digestsQuery.isPending ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse bg-surface rounded-xl p-6 h-48" />
          ))}
        </div>
      ) : digests.length === 0 ? (
        <div className="text-center py-12 text-on-surface-variant">
          <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No digests yet</p>
          <p className="text-sm mt-2">Generate your first digest to get started</p>
        </div>
      ) : (
        <div className="space-y-4">
          {digests.map((digest) => (
            <div key={digest.id} className="bg-surface rounded-xl p-4 sm:p-6 shadow-sm">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    digest.type === 'weekly'
                      ? 'bg-secondary-container text-secondary'
                      : 'bg-primary-container text-primary'
                  }`}
                >
                  {digest.type}
                </span>
                <span className="text-sm text-on-surface-variant">{digestAge(digest.generated_at).label}</span>
              </div>
              {/* Model output is untrusted: no raw HTML, unsafe URL override or remote images. */}
              <div className="text-sm text-on-surface-variant [overflow-wrap:anywhere] [&_p]:my-3 [&_h1]:text-xl [&_h2]:text-lg [&_h3]:text-base [&_:is(h1,h2,h3,h4,h5,h6)]:font-semibold [&_:is(h1,h2,h3,h4,h5,h6)]:mt-4 [&_:is(h1,h2,h3,h4,h5,h6)]:mb-2 [&_ul]:list-disc [&_ol]:list-decimal [&_:is(ul,ol)]:pl-6 [&_li]:my-1 [&_a]:text-primary [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-outline [&_blockquote]:pl-3 [&_pre]:overflow-x-auto [&_pre]:bg-surface-variant [&_pre]:p-3 [&_pre]:rounded-lg [&_code]:font-mono [&_hr]:my-4 [&>*:first-child]:mt-0">
                <Markdown skipHtml disallowedElements={['img']}>{digest.content}</Markdown>
              </div>
              {digest.id === newestWeekly && <OutcomesCheck />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
